<script setup>
import { computed } from 'vue';

const props = defineProps({
    modelValue: { type: Object, default: () => ({ allowed_types: [] }) },
    errors: { type: Array, default: () => [] }, 
});
const emit = defineEmits(['update:modelValue', 'touched']);

const v = computed({
    get: () => props.modelValue || { allowed_types: [] },
    set: (val) => emit('update:modelValue', val),
});

const types = [
    { key: 'retail', labelKey: 'label.retail' },
    { key: 'commercial', labelKey: 'label.commercial' },
];

const selected = computed({
    get: () => (Array.isArray(v.value.allowed_types) ? v.value.allowed_types : []),
    set: (arr) => {
        v.value = { ...v.value, allowed_types: Array.isArray(arr) ? arr : [] };
    },
});

const isSelected = (key) => selected.value.includes(key);

const setSelected = (key, checked) => {
    emit('touched');
    const cur = new Set(selected.value);
    if (checked) {
        cur.add(key);
    } else {
        cur.delete(key);
    }
    selected.value = Array.from(cur);
};

const modelFor = (key) =>
    computed({
        get: () => isSelected(key),
        set: (checked) => setSelected(key, !!checked),
    });

const hasError = computed(() => Array.isArray(props.errors) && props.errors.length > 0);
</script>

<template>
<div class="w-full" :class="{ 'v-input--error': hasError }">
    <p class="font-semibold mb-2">{{ $t('label.customer_types_accepted') }}</p>

    <div class="customer-types-grid">
        <StyledCheckbox
            v-for="t in types"
            :key="t.key"
            v-model="modelFor(t.key).value"
            :name="`customer_types.${t.key}`"
            :label="$t(t.labelKey)"
            hide-details
            density="compact" />
    </div>

    <div
        v-if="hasError"
        class="v-input__details mt-2"
        role="alert"
        aria-live="polite">
        <div class="v-messages">
            <div
                v-for="err in errors"
                :key="err"
                class="v-messages__message">
                {{ err }}
            </div>
        </div>
    </div>
</div>
</template>

<style scoped>
.customer-types-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 6px;
}

@media (min-width: 768px) {
    .customer-types-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        column-gap: 18px;
        row-gap: 6px;
    }
}

.customer-types-grid :deep(.v-input) {
    margin: 0;
}

.customer-types-grid :deep(.v-selection-control) {
    min-height: 0;
}

.customer-types-grid :deep(.v-input__details) {
    display: none;
}
</style>
